import { httpClient } from './http-client';

/**
 * GET /dashboard/summary (issue #27): everything the home dashboard's top
 * sections need, in one call. Money is a decimal string in the household
 * base currency; dates are YYYY-MM-DD. The Go type
 * services.DashboardSummary documents the same shape.
 */

export interface DashboardLink {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
}

export interface AttentionAccount {
  id: string;
  name: string;
  last_activity_on: string | null;
}

export type AttentionSeverity = 'error' | 'warning' | 'info';

export interface AttentionItem {
  kind: string;
  severity: AttentionSeverity;
  title: string;
  detail?: string;
  link: DashboardLink;
  count?: number;
  accounts?: AttentionAccount[];
  more?: number;
}

export interface DashboardChange {
  amount: string;
  percent: string | null;
  from: string;
}

export interface DashboardNetWorth {
  total: string;
  assets: string;
  investments: string;
  debts: string;
  change_30d: DashboardChange | null;
  change_1y: DashboardChange | null;
  last_updated_at: string | null;
  account_count: number;
  sparkline: { date: string; value: string }[];
}

export type BillChipKind =
  | 'paid' | 'no_balance' | 'nothing_due' | 'skipped' | 'overdue' | 'due' | 'scheduled' | 'needs_review' | 'not_seen';

export interface DashboardBill {
  id: string;
  payee_name: string;
  period: string;
  due_date: string | null;
  amount_due: string | null;
  amount_paid: string | null;
  outstanding: string;
  payment_method?: string;
  status: string;
  chip: { kind: BillChipKind; label: string; date?: string };
}

export interface DashboardBills {
  period: string;
  total_billed: string;
  total_paid: string;
  outstanding: string;
  count_paid: number;
  count_open: number;
  items: DashboardBill[];
  upcoming: DashboardBill[];
}

export interface DashboardCategorySpend {
  category_id: string | null;
  name: string;
  color: string | null;
  current: string;
  previous: string;
  avg_3m: string;
  delta: string;
  delta_percent: string | null;
}

export interface DashboardSpending {
  month: string;
  previous_month: string;
  days_elapsed: number;
  days_in_month: number;
  total_current: string;
  total_previous: string;
  total_avg_3m: string;
  categories: DashboardCategorySpend[];
}

export interface DashboardAnalysis {
  id: string;
  title: string;
  created_at: string;
  bullets: string[];
  summary?: string;
}

export interface DashboardCashFlow {
  month: string;
  income: string;
  spend: string;
  savings_transfers: string;
  net: string;
}

export interface DashboardTxn {
  id: string;
  date: string;
  description: string;
  amount: string;
  currency: string;
  account_id: string;
  account_name: string;
  category_name: string;
  category_color: string | null;
  category_group_type: string | null;
}

export interface AccountFreshness {
  account_id: string;
  source: 'plaid' | 'manual';
  /**
   * What days_since counts from: 'sync' (a Plaid chequing/card/loan),
   * 'value' (an investment account's newest valuation) or 'activity'.
   */
  basis?: 'sync' | 'value' | 'activity';
  value_as_of?: string | null;
  last_synced_at: string | null;
  last_activity_on: string | null;
  days_since: number | null;
  stale: boolean;
}

export interface DashboardSummary {
  as_of: string;
  generated_at: string;
  needs_attention: AttentionItem[];
  net_worth: DashboardNetWorth;
  bills: DashboardBills;
  spending: DashboardSpending;
  latest_analysis: DashboardAnalysis | null;
  cash_flow: DashboardCashFlow;
  recent_activity: DashboardTxn[];
  account_freshness: AccountFreshness[];
}

export const dashboardService = {
  getSummary(): Promise<DashboardSummary> {
    return httpClient.get<DashboardSummary>('/dashboard/summary');
  },
};
